import { computed } from 'vue'
import { f7 } from 'framework7-vue'
import i18n from '../i18n.js'
import { useCordovaPlugin } from './useCordovaPlugin.js'
import { parseContact, readContactFile } from '../utils/vcard.js'

// A device contact list can hold thousands of entries; rendering them all into
// the popup at once is slow, so the list is capped and refined by the search.
const RENDER_LIMIT = 150

function t(key, fallback) {
  try {
    const translated = i18n.global.t(key)
    return translated === key ? fallback : translated
  } catch {
    return fallback
  }
}

function escapeHtml(value) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
}

function toast(text) {
  f7.toast.show({ text, closeTimeout: 2500 })
}

/** Flattens a raw contact into the canonical shape used for filling forms. */
function normalizeContact(raw) {
  if (!raw) return null
  const name = raw.name || {}
  const firstName = name.givenName || ''
  const lastName = name.familyName || ''
  const phones = (raw.phones || []).map((p) => p.number).filter(Boolean)
  const emails = (raw.emails || []).map((e) => e.address).filter(Boolean)
  return {
    id: raw.id,
    name: raw.displayName || [firstName, lastName].filter(Boolean).join(' '),
    firstName,
    lastName,
    phones,
    emails,
  }
}

/**
 * One selectable row per phone number, so the user can pick which number to
 * fill. Contacts without a phone still get a row so their name/email can be
 * used.
 */
function toEntries(contacts) {
  const entries = []
  contacts.forEach((raw) => {
    const contact = normalizeContact(raw)
    if (!contact) return
    if (contact.phones.length === 0) {
      entries.push({ contact, phone: '', email: contact.emails[0] || '' })
      return
    }
    contact.phones.forEach((phone) => {
      entries.push({ contact, phone, email: contact.emails[0] || '' })
    })
  })
  return entries
}

// --- form field detection -------------------------------------------------

const FIRST_NAME = /\b(first|given)\s*name\b|\bpr[ée]nom\b|\bvorname\b|\bnombre\b/i
const LAST_NAME = /\b(last|family|sur)\s*name\b|\bnom\s+de\s+famille\b|\bfamilienname\b|\bapellidos?\b/i
const FULL_NAME = /\bfull\s*name\b|\bcomplete\s*name\b|\bnom\s+complet\b|\bnom\s+et\s+pr[ée]nom\b/i
const EMAIL = /\be[-\s]?mail\b|\bcorreo\b|\bcourriel\b/i
// Labels that are exactly these are treated as the field name, which keeps
// patterns like "nom" from matching "Nom du projet".
const EXACT_LAST_NAME = ['nom', 'name', 'lastname', 'last name', 'surname', 'nom de famille', 'familienname', 'apellido', 'apellidos']
const EXACT_FULL_NAME = ['name', 'nom', 'nom complet', 'full name', 'fullname', 'nombre completo']

function describeInput(input) {
  const parts = [
    input.getAttribute('name'),
    input.getAttribute('id'),
    input.getAttribute('placeholder'),
    input.getAttribute('aria-label'),
    input.getAttribute('data-testid'),
  ]
  // Framework7 renders the label as a sibling inside the same .item-inner
  const itemInner = input.closest('.item-inner, .list-item, li, .block')
  const labelEl = itemInner?.querySelector('.item-label, label')
  if (labelEl) parts.push(labelEl.textContent)
  return parts.filter(Boolean).join(' ').replace(/\s+/g, ' ').trim()
}

function resolveScope(input) {
  return (
    input?.closest('form') ||
    input?.closest('.list') ||
    input?.closest('.page-content') ||
    document.body
  )
}

function collectInputs(root, excludeInput) {
  return Array.from(
    root.querySelectorAll('input, textarea'),
  ).filter((el) => {
    if (el === excludeInput) return false
    if (el.disabled || el.readOnly) return false
    const type = (el.getAttribute('type') || el.type || 'text').toLowerCase()
    return ['text', 'email', 'tel', 'number', 'search', ''].includes(type)
  })
}

function matchFields(inputs) {
  const found = {}
  inputs.forEach((input) => {
    const descriptor = describeInput(input)
    if (!descriptor) return
    const exact = descriptor.trim().toLowerCase()
    const has = (key, re) => {
      if (found[key]) return
      if (re.test(descriptor)) found[key] = input
    }
    if (!found.email) has('email', EMAIL)
    has('firstName', FIRST_NAME)
    has('lastName', LAST_NAME)
    if (EXACT_LAST_NAME.includes(exact)) has('lastName', /$^/)
    if (EXACT_FULL_NAME.includes(exact)) has('fullName', /$^/)
  })
  return found
}

function setInputValue(input, value) {
  if (!input || value === undefined || value === null) return false
  const text = String(value).trim()
  if (!text) return false
  // Assign through the native setter so framework bindings (Vue v-model on
  // F7 list inputs) observe the change.
  const prototype = Object.getPrototypeOf(input)
  const setter = Object.getOwnPropertyDescriptor(prototype, 'value')?.set
  if (setter) setter.call(input, text)
  else input.value = text
  input.dispatchEvent(new Event('input', { bubbles: true }))
  input.dispatchEvent(new Event('change', { bubbles: true }))
  return true
}

function isEmpty(input) {
  return !String(input?.value ?? '').trim()
}

/**
 * Fills the contact's first name, last name and email into the matching inputs
 * of the surrounding form. Only empty fields are written, so anything the user
 * already typed is kept.
 */
function fillContactFields(contact, { excludeInput, onlyEmpty = true } = {}) {
  const root = resolveScope(excludeInput)
  if (!root) return []
  const fields = matchFields(collectInputs(root, excludeInput))
  const filled = []

  const assign = (input, value, key) => {
    if (!input || !value) return
    if (onlyEmpty && !isEmpty(input)) return
    if (setInputValue(input, value)) filled.push(key)
  }

  if (fields.firstName || fields.lastName) {
    assign(fields.firstName, contact.firstName, 'firstName')
    assign(fields.lastName, contact.lastName, 'lastName')
  } else {
    assign(fields.fullName, contact.name, 'fullName')
  }
  assign(fields.email, contact.emails?.[0], 'email')

  return filled
}

function applyPhoneNumber(iti, input, phone) {
  if (!input || !phone) return false
  const cleaned = String(phone).replace(/[^\d+]/g, '')
  if (!cleaned) return false
  try {
    if (iti) {
      // Numbers without "+" are national, so they are parsed with the country
      // that is currently selected in the field.
      const iso2 = cleaned.startsWith('+') ? undefined : iti.getSelectedCountry()?.iso2
      iti.setNumber(cleaned, iso2)
    }
  } catch (error) {
    console.warn('[useContactFill] could not set number through intl-tel-input', error)
  }
  if (!String(input.value || '').trim()) {
    setInputValue(input, cleaned)
  }
  return !!String(input.value || '').trim()
}

// --- pickers --------------------------------------------------------------

/**
 * Searchable contact list built with plain DOM (Framework7 renders the popup;
 * Vue directives in raw content are not compiled). Resolves with the chosen
 * { contact, phone } entry or null when dismissed.
 */
function openContactListPopup(entries, { title, hint }) {
  return new Promise((resolve) => {
    let result = null
    const popup = f7.popup.create({
      content: `
        <div class="popup contact-fill-popup">
          <div class="view view-root">
            <div class="page">
              <div class="navbar">
                <div class="navbar-bg"></div>
                <div class="navbar-inner">
                  <div class="title">${escapeHtml(title)}</div>
                  <div class="right">
                    <a href="#" class="link popup-close">${escapeHtml(
                      t('contactFill.cancel', 'Cancel'),
                    )}</a>
                  </div>
                </div>
              </div>
              <div class="page-content">
                <div class="searchbar">
                  <input type="search" class="cf-search" placeholder="${escapeHtml(
                    t('contactFill.search', 'Search contacts'),
                  )}" />
                </div>
                ${
                  hint
                    ? `<div class="block block-strong cf-hint">${escapeHtml(hint)}</div>`
                    : ''
                }
                <div class="list">
                  <ul class="cf-list"></ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      `,
      on: {
        popupClosed: () => resolve(result),
      },
    })

    popup.open()

    const $el = popup.$el
    const listEl = $el.find('.cf-list')[0]
    const searchEl = $el.find('.cf-search')[0]
    // The rows only carry the index inside the currently displayed slice, so the
    // displayed slice has to be remembered for the click handler.
    let visible = []

    const render = (term = '') => {
      const needle = term.trim().toLowerCase()
      const matches = needle
        ? entries.filter((entry) =>
            [entry.contact.name, entry.phone, entry.email]
              .filter(Boolean)
              .some((value) => String(value).toLowerCase().includes(needle)),
          )
        : entries
      visible = matches.slice(0, RENDER_LIMIT)

      if (visible.length === 0) {
        listEl.innerHTML = `
          <li class="item-content">
            <div class="item-inner">
              <div class="item-title">${escapeHtml(
                t('contactFill.noResults', 'No contact found'),
              )}</div>
            </div>
          </li>`
        return
      }

      listEl.innerHTML = visible
        .map(
          (entry, index) => `
          <li class="item-content cf-row link" data-index="${index}">
            <div class="item-media"><i class="f7-icons">person_circle</i></div>
            <div class="item-inner">
              <div class="item-title">${escapeHtml(entry.contact.name || t('contactFill.unknown', 'Unknown'))}</div>
              <div class="item-text">${escapeHtml(entry.phone || entry.email || '')}</div>
            </div>
          </li>`,
        )
        .join('')

      if (matches.length > visible.length) {
        listEl.insertAdjacentHTML(
          'beforeend',
          `<li class="item-content">
            <div class="item-inner">
              <div class="item-text">${escapeHtml(
                t('contactFill.refineSearch', 'Refine your search to see more contacts'),
              )}</div>
            </div>
          </li>`,
        )
      }
    }

    $el.on('click', '.cf-row', (e) => {
      e.preventDefault()
      const index = Number(e.currentTarget?.getAttribute('data-index'))
      const entry = visible[index]
      if (!entry) return
      result = entry
      popup.close()
    })

    searchEl?.addEventListener('input', (e) => render(e.target.value))

    render()
  })
}

/** Asks which number to use when a contact has more than one. */
function openNumberChooser(contact, { title } = {}) {
  return new Promise((resolve) => {
    let result = null
    const popup = f7.popup.create({
      content: `
        <div class="popup contact-fill-popup">
          <div class="view view-root">
            <div class="page">
              <div class="navbar">
                <div class="navbar-bg"></div>
                <div class="navbar-inner">
                  <div class="title">${escapeHtml(
                    title || t('contactFill.chooseNumber', 'Choose a number'),
                  )}</div>
                  <div class="right">
                    <a href="#" class="link popup-close">${escapeHtml(
                      t('contactFill.cancel', 'Cancel'),
                    )}</a>
                  </div>
                </div>
              </div>
              <div class="page-content">
                <div class="block block-strong">
                  <p>${escapeHtml(contact.name)}</p>
                </div>
                <div class="list">
                  <ul class="cf-numbers">
                    ${contact.phones
                      .map(
                        (phone, index) => `
                      <li class="item-content link cf-number" data-index="${index}">
                        <div class="item-media"><i class="f7-icons">phone_fill</i></div>
                        <div class="item-inner">
                          <div class="item-title">${escapeHtml(phone)}</div>
                        </div>
                      </li>`,
                      )
                      .join('')}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      `,
      on: {
        popupClosed: () => resolve(result),
      },
    })

    popup.open()

    popup.$el.on('click', '.cf-number', (e) => {
      e.preventDefault()
      const index = Number(e.currentTarget?.getAttribute('data-index'))
      const phone = contact.phones[index]
      if (!phone) return
      result = { contact, phone, email: contact.emails?.[0] || '' }
      popup.close()
    })
  })
}

/** Wraps a raw contact into the { contact, phone } entry shape used to fill. */
function toEntry(raw) {
  const contact = normalizeContact(raw)
  if (!contact) return null
  return {
    contact,
    phone: contact.phones[0] || '',
    email: contact.emails[0] || '',
  }
}

/**
 * Fallback for browsers with no address book access (Safari/iOS, Firefox): the
 * user shares a contact from their phone as a vCard (.vcf) or pastes the
 * details. Resolves with the { contact, phone } entry or null.
 */
function openContactImportSheet() {
  return new Promise((resolve) => {
    let result = null
    const errorText = t('contactFill.invalidContact', 'Could not read a contact from that file')
    const popup = f7.popup.create({
      content: `
        <div class="popup contact-fill-popup">
          <div class="view view-root">
            <div class="page">
              <div class="navbar">
                <div class="navbar-bg"></div>
                <div class="navbar-inner">
                  <div class="title">${escapeHtml(
                    t('contactFill.importTitle', 'Import a contact'),
                  )}</div>
                  <div class="right">
                    <a href="#" class="link popup-close">${escapeHtml(
                      t('contactFill.cancel', 'Cancel'),
                    )}</a>
                  </div>
                </div>
              </div>
              <div class="page-content">
                <div class="block block-strong cf-hint">
                  <p>${escapeHtml(
                    t(
                      'contactFill.importHint',
                      'This browser cannot open your address book. In the Contacts app of your phone, share a contact as a vCard file, or paste the details below.',
                    ),
                  )}</p>
                </div>
                <div class="list">
                  <ul>
                    <li>
                      <div class="item-content item-input-wrap">
                        <div class="item-inner">
                          <div class="item-input">
                            <input type="file" class="cf-file" accept=".vcf,text/vcard,text/x-vcard,text/plain" />
                          </div>
                          <div class="item-label">${escapeHtml(
                            t('contactFill.chooseFile', 'Contact file (.vcf)'),
                          )}</div>
                        </div>
                      </div>
                    </li>
                  </ul>
                </div>
                <div class="list">
                  <ul>
                    <li>
                      <div class="item-content item-input-wrap">
                        <div class="item-inner">
                          <div class="item-input">
                            <textarea class="cf-paste" rows="3" placeholder="${escapeHtml(
                              t(
                                'contactFill.pastePlaceholder',
                                'Aya Kone, +225 07 01 23 45 67, aya@example.com',
                              ),
                            )}"></textarea>
                          </div>
                          <div class="item-label">${escapeHtml(
                            t('contactFill.pasteLabel', 'Or paste the details'),
                          )}</div>
                        </div>
                      </div>
                    </li>
                  </ul>
                </div>
                <div class="cf-error" style="display:none;color:#C0440A;font-size:12px;padding:0 16px;">
                  ${escapeHtml(errorText)}
                </div>
                <div class="block">
                  <a href="#" class="button button-fill button-round cf-fill">${escapeHtml(
                    t('contactFill.fill', 'Fill'),
                  )}</a>
                </div>
              </div>
            </div>
          </div>
        </div>
      `,
      on: {
        popupClosed: () => resolve(result),
      },
    })

    popup.open()

    const $el = popup.$el
    const fileEl = $el.find('.cf-file')[0]
    const textEl = $el.find('.cf-paste')[0]
    const errorEl = $el.find('.cf-error')[0]
    let fileText = ''

    const showError = () => {
      if (errorEl) errorEl.style.display = 'block'
    }
    const hideError = () => {
      if (errorEl) errorEl.style.display = 'none'
    }

    fileEl?.addEventListener('change', async (event) => {
      const file = event.target?.files?.[0]
      fileText = ''
      if (!file) return
      hideError()
      try {
        fileText = await readContactFile(file)
      } catch (error) {
        console.warn('[useContactFill] could not read the contact file', error)
        showError()
        return
      }
      if (!parseContact(fileText)) showError()
    })

    textEl?.addEventListener('input', hideError)

    $el.on('click', '.cf-fill', (e) => {
      e.preventDefault()
      const entry = toEntry(parseContact(fileText || textEl?.value))
      if (!entry) {
        showError()
        return
      }
      result = entry
      popup.close()
    })
  })
}

export function useContactFill() {
  const { isNative, webPickerSupported, listContacts, pickContacts } = useCordovaPlugin()

  // A vCard can be imported anywhere a file input exists, which is what keeps
  // the feature usable on Safari/iOS where `navigator.contacts` is missing.
  const canImportFile = computed(() => typeof window !== 'undefined' && typeof window.File !== 'undefined')

  const contactsAvailable = computed(
    () => isNative.value || webPickerSupported.value || canImportFile.value,
  )

  /**
   * Opens the contact list and resolves with { contact, phone } or null.
   *
   * Three strategies, in order:
   *  1. Native: read the address book and show the searchable list.
   *  2. Web with the Contact Picker API (Chromium): hand over to the platform
   *     picker. This has to start inside a click handler, so it is called before
   *     anything is awaited.
   *  3. Anything else — Safari/iOS, Firefox, desktop Chrome: no address book
   *     access is possible, so fall back to importing a vCard or pasted text.
   */
  async function pickContact() {
    if (!contactsAvailable.value) {
      toast(t('contactFill.notAvailable', 'Contacts are not available on this device'))
      return null
    }

    if (isNative.value) {
      const contacts = await listContacts()
      if (contacts.length === 0) {
        toast(t('contactFill.noContacts', 'No contact found on this device'))
        return null
      }
      return openContactListPopup(toEntries(contacts), {
        title: t('contactFill.selectContact', 'Select a contact'),
        hint: t('contactFill.listHint', 'Pick a contact to fill the form for you'),
      })
    }

    if (webPickerSupported.value) {
      try {
        const picked = await pickContacts({ multiple: false })
        if (picked.length === 0) return null
        const contact = normalizeContact(picked[0])
        if (!contact) return null
        if (contact.phones.length > 1) return openNumberChooser(contact)
        return toEntry(picked[0])
      } catch (ex) {
        // AbortError: the user closed the picker. NotSupportedError: the browser
        // exposes the API but not the properties we need.
        if (ex?.name === 'AbortError') return null
        console.warn('[useContactFill] contact picker failed, falling back to import', ex)
      }
    }

    return openContactImportSheet()
  }

  /**
   * Fills the telephone field from a device contact, plus first name, last
   * name and email when the surrounding form has matching empty inputs.
   */
  async function fillFromContacts({ input, iti = null, fillDetails = true } = {}) {
    const picked = await pickContact()
    if (!picked) return null

    const { contact, phone } = picked
    const phoneFilled = applyPhoneNumber(iti, input, phone)
    const fields = fillDetails
      ? fillContactFields(contact, { excludeInput: input })
      : []

    if (fields.length > 0) {
      toast(t('contactFill.filledDetails', 'Contact details filled in'))
    } else if (phoneFilled) {
      toast(t('contactFill.filledPhone', 'Phone number filled from contact'))
    }

    return { contact, phone: phoneFilled, fields }
  }

  /**
   * Injects the contact button at the end of a telephone field. Returns a
   * teardown function; the button lives inside the intl-tel-input wrapper, so it
   * is removed automatically when the instance is destroyed.
   */
  function attachContactButton(input, onClick) {
    if (!contactsAvailable.value) return () => {}

    const wrapper = input?.closest('.iti')
    if (!wrapper) return () => {}

    const existing = wrapper.querySelector('.iti-contact-button')
    if (existing) existing.remove()

    const label = t('contactFill.buttonLabel', 'Fill from contacts')
    const button = document.createElement('button')
    button.type = 'button'
    button.className = 'iti-contact-button'
    button.setAttribute('aria-label', label)
    button.dataset.tip = t('contactFill.buttonTip', 'Fill from your contacts')
    button.innerHTML = '<i class="f7-icons">person_badge_plus_fill</i>'
    button.addEventListener('click', (event) => {
      event.preventDefault()
      event.stopPropagation()
      onClick()
    })

    wrapper.appendChild(button)
    wrapper.classList.add('iti--has-contact-button')

    return () => {
      button.remove()
      wrapper.classList.remove('iti--has-contact-button')
    }
  }

  return {
    contactsAvailable,
    pickContact,
    fillFromContacts,
    fillContactFields,
    attachContactButton,
  }
}
